import { useState } from 'react';
import * as XLSX from 'xlsx';
import { Modal, Button, Input, Select, useToast } from '../../../../shared/components';
import { getResultsRepository } from '../../../../shared/services/results/repositoryFactory';
import { CreateImportBatchResult, SnapshotRowInput, TipoCuenta, MONTH_NAMES_ES } from '../../../../shared/services/results/types';

const repo = getResultsRepository();
const money = (v: number) => v.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

const MONTH_LABELS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

interface DetectedSheet {
  sheetName: string;
  headerRowIndex: number; // fila (0-based) donde está "Cuenta"
  cuentaCol: number; // columna (0-based) donde está "Cuenta"
}

/**
 * Recorre TODAS las hojas del archivo (sin importar cómo se llamen - el
 * archivo puede traer 1 o varias, con nombres impredecibles, ej.
 * "AUX MES" + "PYG MES") y elige la que tenga la estructura real de
 * Ingresos/Egresos: una celda "Cuenta" seguida de "Descripción", CON
 * VARIAS columnas de cliente a la derecha (≥5) y una fila de nombres justo
 * arriba. Esa exigencia extra es clave para no confundirse con una hoja
 * "datos tabla" de formato antiguo, que también tiene "Cuenta"/"Descripción"
 * pero como tabla plana (una fila por cuenta+cliente, sin grilla). Cualquier
 * otra hoja (como un Estado de Pérdidas y Ganancias por centro de costo) se
 * ignora automáticamente.
 */
function detectResultsSheet(wb: XLSX.WorkBook): { detected: DetectedSheet; raw: unknown[][] } | null {
  const MIN_CLIENT_COLUMNS = 5;
  const populatedCountAfter = (row: unknown[] | undefined, fromCol: number) =>
    row ? row.slice(fromCol).filter((v) => v !== 0 && v !== null && v !== undefined && String(v).trim() !== '').length : 0;

  for (const sheetName of wb.SheetNames) {
    const raw: unknown[][] = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, defval: 0, blankrows: true });
    for (let r = 0; r < raw.length; r++) {
      const row = raw[r];
      for (let c = 0; c < row.length; c++) {
        const cell = String(row[c] ?? '').trim().toLowerCase();
        const next = String(row[c + 1] ?? '').trim().toLowerCase();
        if (cell !== 'cuenta' || (next !== 'descripción' && next !== 'descripcion')) continue;
        const sameRowClientCols = populatedCountAfter(row, c + 2);
        const aboveRowClientCols = populatedCountAfter(raw[r - 1], c + 2);
        if (sameRowClientCols >= MIN_CLIENT_COLUMNS || aboveRowClientCols >= MIN_CLIENT_COLUMNS) {
          return { detected: { sheetName, headerRowIndex: r, cuentaCol: c }, raw };
        }
      }
    }
  }
  return null;
}

/**
 * Parsea la hoja detectada: cuentas de INGRESOS/EGRESOS + las filas de
 * control que trae el propio archivo (TOTAL INGRESOS:/TOTAL EGRESOS:/
 * UTILIDAD:) - estas últimas se conservan para mostrarlas igual que en el
 * Excel y para que la app pueda comparar su propia suma contra ellas.
 */
/** `sheet_to_json` con `defval: 0` rellena celdas de TEXTO vacías con el número 0 - hay que tratarlas como vacías. */
function cellText(row: unknown[], col: number): string {
  const v = row[col];
  if (v === 0 || v === undefined || v === null) return '';
  return String(v).trim();
}

function parseResultsSheet(raw: unknown[][], headerRowIndex: number, cuentaCol: number): SnapshotRowInput[] {
  const descCol = cuentaCol + 1;
  const clientNamesRow = raw[headerRowIndex - 1];
  const columns: { index: number; name: string }[] = [];
  for (let j = descCol + 1; j < clientNamesRow.length; j++) {
    const name = cellText(clientNamesRow, j);
    if (name) columns.push({ index: j, name });
  }
  if (columns.length === 0) throw new Error('No se encontraron columnas de cliente en la hoja de resultados.');

  const result: SnapshotRowInput[] = [];
  let current: TipoCuenta | null = null;
  let utilidadCaptured = false;
  for (let i = headerRowIndex + 1; i < raw.length; i++) {
    const row = raw[i];
    const colA = cellText(row, cuentaCol);
    const colB = cellText(row, descCol);
    if (/^ingresos$/i.test(colA)) {
      current = 'INGRESO';
      continue;
    }
    if (/^egresos$/i.test(colA)) {
      current = 'GASTOS';
      continue;
    }
    if (/^total\s*ingresos/i.test(colB)) {
      const values: Record<string, number> = {};
      for (const col of columns) values[col.name] = Number(row[col.index]) || 0;
      result.push({ accountCode: 'TOTAL INGRESOS', accountDescription: 'Total Ingresos (según archivo)', tipoCuenta: 'INGRESO', rowType: 'SECTION_TOTAL', values });
      continue;
    }
    if (/^total\s*egresos/i.test(colB)) {
      const values: Record<string, number> = {};
      for (const col of columns) values[col.name] = Number(row[col.index]) || 0;
      result.push({ accountCode: 'TOTAL EGRESOS', accountDescription: 'Total Egresos (según archivo)', tipoCuenta: 'GASTOS', rowType: 'SECTION_TOTAL', values });
      continue;
    }
    if (/^utilidad/i.test(colA) && !utilidadCaptured) {
      const values: Record<string, number> = {};
      for (const col of columns) values[col.name] = Number(row[col.index]) || 0;
      // Solo se toma la primera fila UTILIDAD con datos reales (algunos archivos traen una segunda fila
      // "plantilla" del mes siguiente, vacía, que debe ignorarse).
      if (Object.values(values).some((v) => v !== 0)) {
        result.push({ accountCode: 'UTILIDAD', accountDescription: colA, tipoCuenta: null, rowType: 'UTILIDAD', values });
        utilidadCaptured = true;
      }
      continue;
    }
    if (!colA || !colB || !current) continue;
    const values: Record<string, number> = {};
    for (const col of columns) values[col.name] = Number(row[col.index]) || 0;
    result.push({ accountCode: colA, accountDescription: colB, tipoCuenta: current, rowType: 'ACCOUNT', values });
  }
  return result;
}

function detectMonth(fileName: string, wb: XLSX.WorkBook, raw: unknown[][]): string | null {
  const headerText = raw
    .slice(0, 8)
    .flat()
    .map((c) => String(c ?? ''))
    .join(' ');
  const source = `${fileName} ${wb.SheetNames.join(' ')} ${headerText}`.toLowerCase();
  return MONTH_NAMES_ES.find((m) => source.includes(m)) ?? null;
}

export function ImportResultsModal({ open, onClose, onImported }: { open: boolean; onClose: () => void; onImported: (result: CreateImportBatchResult) => void }) {
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [month, setMonth] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [detectedSheetName, setDetectedSheetName] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState<{ snapshotRows: SnapshotRowInput[]; sheetName: string; totalIngresos: number; totalGastos: number } | null>(null);

  const onFileChange = async (f: File | null) => {
    setFile(f);
    setDetectedSheetName(null);
    if (!f) return;
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const found = detectResultsSheet(wb);
      if (found) {
        setDetectedSheetName(found.detected.sheetName);
        const detected = detectMonth(f.name, wb, found.raw);
        if (detected) setMonth(detected);
      }
    } catch {
      // Se valida en detalle al importar - aquí solo intentamos autocompletar.
    }
  };

  const close = () => {
    setFile(null);
    setMonth('');
    setDetectedSheetName(null);
    setConfirming(false);
    setPending(null);
    onClose();
  };

  /** Paso 1: parsea el archivo y muestra el mes/año detectados para que el usuario confirme antes de guardar nada. */
  const parseAndAskConfirmation = async () => {
    if (!file || !month) {
      toast.show('Selecciona el archivo y confirma el mes.', 'error');
      return;
    }
    setIsSubmitting(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const found = detectResultsSheet(wb);
      if (!found) throw new Error('Ninguna hoja del archivo tiene la estructura de Ingresos/Egresos (columnas "Cuenta" y "Descripción").');
      const { detected, raw } = found;
      const snapshotRows = parseResultsSheet(raw, detected.headerRowIndex, detected.cuentaCol);
      if (!snapshotRows.some((r) => r.rowType === 'ACCOUNT')) throw new Error(`No se encontraron cuentas en la hoja "${detected.sheetName}".`);

      const totalIngresos = snapshotRows.find((r) => r.rowType === 'SECTION_TOTAL' && r.tipoCuenta === 'INGRESO')?.values['TOTALES'] ?? snapshotRows.filter((r) => r.rowType === 'ACCOUNT' && r.tipoCuenta === 'INGRESO').reduce((s, r) => s + (r.values['TOTALES'] ?? 0), 0);
      const totalGastos = snapshotRows.find((r) => r.rowType === 'SECTION_TOTAL' && r.tipoCuenta === 'GASTOS')?.values['TOTALES'] ?? snapshotRows.filter((r) => r.rowType === 'ACCOUNT' && r.tipoCuenta === 'GASTOS').reduce((s, r) => s + (r.values['TOTALES'] ?? 0), 0);

      setPending({ snapshotRows, sheetName: detected.sheetName, totalIngresos, totalGastos });
      setConfirming(true);
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo leer el archivo.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Paso 2: solo se guarda en base de datos si el usuario confirma que el mes/año son correctos. */
  const confirmAndImport = async () => {
    if (!pending || !file) return;
    setIsSubmitting(true);
    try {
      const result = await repo.createImportBatch({ sourceMonth: month, sourceYear: year, fileName: file.name, sheetNameUsed: pending.sheetName, snapshotRows: pending.snapshotRows });
      toast.show(
        result.validationErrors.length === 0 ? 'Archivo importado sin datos por revisar.' : `Archivo importado con ${result.validationErrors.length} fila(s) por revisar — verifícalas abajo.`,
        result.validationErrors.length === 0 ? 'success' : 'error'
      );
      onImported(result);
      close();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo importar el archivo.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={close} title={confirming ? '¿Es correcto este mes?' : 'Cargar resultados del mes'}>
      {!confirming ? (
        <div className="space-y-4">
          <label className="block w-full">
            <span className="mb-1.5 block text-sm font-medium text-ink-700">Archivo de Ingresos/Egresos del mes</span>
            <input type="file" accept=".xlsx,.xls" onChange={(e) => onFileChange(e.target.files?.[0] ?? null)} className="block w-full text-sm text-ink-600" />
          </label>

          {file && (
            <p className="text-xs text-ink-400">
              {detectedSheetName ? (
                <>
                  Hoja detectada automáticamente: <span className="font-medium text-ink-600">"{detectedSheetName}"</span>. Si el archivo trae otras hojas (por ejemplo un Estado de Pérdidas y
                  Ganancias), se ignoran.
                </>
              ) : (
                'No se detectó todavía una hoja con estructura de Ingresos/Egresos - revisa el archivo.'
              )}
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Select label="Mes" placeholder="Selecciona el mes" options={MONTH_NAMES_ES.map((m, i) => ({ value: m, label: MONTH_LABELS[i] }))} value={month} onChange={(e) => setMonth(e.target.value)} />
            <Input label="Año" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
          </div>

          <p className="text-xs text-ink-400">
            El archivo se lee en tu navegador. Se guarda la grilla completa (incluidas las filas TOTAL INGRESOS/TOTAL EGRESOS/UTILIDAD tal como vienen), se genera el detalle para "Datos Tabla" usando
            el Tipo de Servicio configurado por cliente en Parámetros, y se revisan automáticamente los datos dudosos.
          </p>

          <Button fullWidth disabled={!file || !month} isLoading={isSubmitting} onClick={parseAndAskConfirmation}>
            Importar
          </Button>
        </div>
      ) : (
        pending && (
          <div className="space-y-4">
            <p className="text-sm text-ink-600">
              Estás a punto de guardar este archivo como <span className="font-semibold text-ink-800">{MONTH_LABELS[MONTH_NAMES_ES.indexOf(month)] ?? month} {year}</span>. Verifica que sea
              correcto antes de continuar - el mes seleccionado y el que realmente trae el archivo pueden no coincidir.
            </p>

            <div className="space-y-2 rounded-xl border border-ink-100 bg-ink-50/60 p-4 text-sm">
              <div className="flex justify-between gap-3">
                <span className="text-ink-500">Archivo</span>
                <span className="text-right font-medium text-ink-800">{file?.name}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-ink-500">Hoja detectada</span>
                <span className="text-right font-medium text-ink-800">{pending.sheetName}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-ink-500">Mes a procesar</span>
                <span className="text-right font-semibold text-ink-800">
                  {MONTH_LABELS[MONTH_NAMES_ES.indexOf(month)] ?? month} {year}
                </span>
              </div>
              <div className="my-2 border-t border-ink-200" />
              <div className="flex justify-between gap-3">
                <span className="text-ink-500">Total Ingresos (según archivo)</span>
                <span className="text-right font-semibold text-brand-700">{money(pending.totalIngresos)}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-ink-500">Total Gastos (según archivo)</span>
                <span className="text-right font-semibold text-critical">{money(pending.totalGastos)}</span>
              </div>
            </div>

            <p className="text-xs text-ink-400">
              Compara estos totales con lo que esperas para {MONTH_LABELS[MONTH_NAMES_ES.indexOf(month)] ?? month} - si no coinciden, probablemente elegiste el mes equivocado o el archivo no es
              el correcto. Cancela y revisa antes de continuar.
            </p>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" fullWidth onClick={() => setConfirming(false)} disabled={isSubmitting}>
                Cancelar, revisar de nuevo
              </Button>
              <Button fullWidth isLoading={isSubmitting} onClick={confirmAndImport}>
                Sí, procesar este mes
              </Button>
            </div>
          </div>
        )
      )}
    </Modal>
  );
}
